import { useRef, useEffect, useState } from 'react';
import { features } from '../../../mocks/homeData';

const keyBenefits = [
  {
    id: 1,
    title: "독소 및 중금속 제거",
    desc: "과일과 채소 표면의 농약 잔류물과 중금속을 효과적으로 흡착하여 자연스럽게 제거합니다.",
  },
  {
    id: 2,
    title: "불쾌한 냄새 및 화학 잔류물 중화",
    desc: "농산물의 맛과 향에 영향을 줄 수 있는 유해 물질을 흡착하여 신선함을 유지합니다.",
  },
  {
    id: 3,
    title: "미생물 오염 물질 감소",
    desc: "과일과 채소 표면의 세균 및 곰팡이를 기계적으로 제거하는 데 효과적으로 도움을 줍니다.",
  },
  {
    id: 4,
    title: "매일 안전하게 사용 가능한 천연 성분",
    desc: "가정에서 매일 정기적으로 사용하기에 적합한 100% 천연 제품입니다.",
  },
  {
    id: 5,
    title: "선택적 작용",
    desc: "세척 중 과일과 채소의 영양 성분을 손상시키지 않으면서 유해 물질만을 선택적으로 제거합니다.",
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [openItem, setOpenItem] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="bg-[#F5F0E8] py-24 px-6" ref={sectionRef}>
      <div className="max-w-5xl mx-auto">
        <div className={`text-center mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-[#8B7355] text-xs font-medium tracking-[0.3em] uppercase mb-3">Why Within</p>
          <h2 className="text-[#2C2825] text-4xl lg:text-5xl font-light mb-4">
            왜 위딘인가요?
          </h2>
          <p className="text-[#6B5F52] text-base max-w-lg mx-auto">
            지올라이트의 강력한 자연 치유력과 위딘의 엄격한 품질 기준이 만나<br />당신의 웰니스 여정을 책임집니다
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-4">
          {features.map((feature, i) => (
            <div
              key={feature.id}
              className={`flex flex-col items-center text-center transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 flex items-center justify-center rounded-full bg-[#E8E0D4] mb-4 sm:mb-5 hover:bg-[#D9CEBD] transition-colors duration-300">
                <i className={`${feature.icon} text-[1.6rem] sm:text-[2rem] text-[#2C2825]`}></i>
              </div>
              <p className="text-[#2C2825] text-xs sm:text-sm leading-snug">
                {feature.title}
              </p>
            </div>
          ))}
        </div>

        {/* KEY BENEFITS */}
        <div className={`mt-20 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-[#8B7355] text-xs font-medium tracking-[0.3em] uppercase mb-8">KEY BENEFITS</p>
          <div className="divide-y divide-[#D9CFC3]">
            {keyBenefits.map((item) => (
              <div key={item.id} className="py-5">
                <button
                  className="w-full flex items-center justify-between text-left cursor-pointer group"
                  onClick={() => setOpenItem(openItem === item.id ? null : item.id)}
                >
                  <span
                    className="text-[#2C2825] text-base font-medium group-hover:text-[#8B7355] transition-colors duration-200"
                    style={{ fontSize: '1.1rem' }}
                  >
                    {item.title}
                  </span>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-4">
                    <i className={`${openItem === item.id ? 'ri-close-line' : 'ri-add-line'} text-[#8B7355] text-xl`}></i>
                  </span>
                </button>
                {openItem === item.id && (
                  <p className="mt-3 text-[#6B5F52] text-sm leading-relaxed pr-10">
                    {item.desc}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={`mt-16 bg-[#2C2825] rounded-2xl p-7 sm:p-10 flex flex-col lg:flex-row items-center gap-8 lg:gap-10 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex-1 text-center lg:text-left">
            <p className="text-[#C4A882] text-xs tracking-[0.3em] uppercase mb-2">지올라이트란?</p>
            <h3 className="text-white text-2xl lg:text-3xl font-light mb-4">
              수백만 년의 지구가 만든<br />자연의 정화 미네랄
            </h3>
            <p className="text-white/60 text-sm leading-relaxed">
              지올라이트는 화산암과 알칼리성 지하수가 수백만 년에 걸쳐 반응하여 형성된 천연 미네랄입니다.
              독특한 나노 다공성 결정 구조가 체내 독소, 중금속, 유해 물질을 흡착하여 자연스럽게 배출을 돕습니다.
            </p>
          </div>
          <div className="flex-shrink-0 w-full sm:w-60 h-52 sm:h-60 lg:w-72 lg:h-72 rounded-2xl overflow-hidden border border-white/10">
            <img
              src="https://static.readdy.ai/image/72a3391a4cd99fb7eb3bf082bc970d85/5cdf244c31fa3189ecfe215dfd9917b4.jpeg"
              alt="천연 지올라이트 원석"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
